/**
 * Stock Screener API client for Cipher (Phase 4 Expansion)
 * Supports 85+ filter criteria, saved screens, CSV/Excel export
 */
import { apiClient } from './client'

// =============================================================================
// Types
// =============================================================================

export interface ScreenerStock {
  ticker: string
  name: string
  price: number
  change: number
  change_pct: number
  volume: number
  avg_volume: number
  market_cap: number
  // Valuation
  pe_ratio: number | null
  forward_pe: number | null
  pb_ratio: number | null
  ps_ratio: number | null
  peg_ratio: number | null
  ev_ebitda: number | null
  ev_revenue: number | null
  price_to_fcf: number | null
  eps: number | null
  // Dividends
  dividend_yield: number
  payout_ratio: number | null
  // Profitability
  gross_margin: number | null
  operating_margin: number | null
  profit_margin: number | null
  roe: number | null
  roa: number | null
  roic: number | null
  fcf_margin: number | null
  // Growth
  revenue_growth: number | null
  earnings_growth: number | null
  // Financial Health
  debt_to_equity: number | null
  current_ratio: number | null
  quick_ratio: number | null
  interest_coverage: number | null
  // Technical
  beta: number | null
  rsi: number | null
  week52_high_pct: number | null
  high_52w: number
  low_52w: number
  above_sma_50: boolean
  above_sma_200: boolean
  // Ownership
  institutional_ownership: number | null
  insider_ownership: number | null
  short_interest: number | null
  // Descriptive
  sector: string
  industry: string
  country: string
  exchange: string
  // Cipher Scores
  cipher_score: number
  momentum_score: number
  insider_score: number
  sentiment_score: number
}

export interface ScreenFilters {
  // Descriptive
  sector?: string
  industry?: string
  country?: string
  exchange?: string
  // Price & Volume
  price_min?: number
  price_max?: number
  volume_min?: number
  volume_max?: number
  avg_volume_min?: number
  avg_volume_max?: number
  market_cap_min?: number
  market_cap_max?: number
  price_change_pct_min?: number
  price_change_pct_max?: number
  // Valuation
  pe_min?: number
  pe_max?: number
  forward_pe_min?: number
  forward_pe_max?: number
  pb_min?: number
  pb_max?: number
  ps_min?: number
  ps_max?: number
  peg_min?: number
  peg_max?: number
  ev_ebitda_min?: number
  ev_ebitda_max?: number
  ev_revenue_min?: number
  ev_revenue_max?: number
  price_to_fcf_min?: number
  price_to_fcf_max?: number
  // Dividends
  dividend_yield_min?: number
  dividend_yield_max?: number
  payout_ratio_min?: number
  payout_ratio_max?: number
  // Profitability
  gross_margin_min?: number
  gross_margin_max?: number
  operating_margin_min?: number
  operating_margin_max?: number
  profit_margin_min?: number
  profit_margin_max?: number
  roe_min?: number
  roe_max?: number
  roa_min?: number
  roa_max?: number
  roic_min?: number
  roic_max?: number
  fcf_margin_min?: number
  fcf_margin_max?: number
  // Growth
  revenue_growth_min?: number
  revenue_growth_max?: number
  earnings_growth_min?: number
  earnings_growth_max?: number
  // Financial Health
  debt_to_equity_min?: number
  debt_to_equity_max?: number
  current_ratio_min?: number
  current_ratio_max?: number
  quick_ratio_min?: number
  quick_ratio_max?: number
  interest_coverage_min?: number
  interest_coverage_max?: number
  // Technical
  beta_min?: number
  beta_max?: number
  rsi_min?: number
  rsi_max?: number
  week52_high_pct_min?: number
  week52_high_pct_max?: number
  above_sma_50?: boolean
  above_sma_200?: boolean
  volume_surge?: boolean
  // Ownership
  insider_ownership_min?: number
  insider_ownership_max?: number
  institutional_ownership_min?: number
  institutional_ownership_max?: number
  short_interest_min?: number
  short_interest_max?: number
  // Cipher Score
  apex_score_min?: number
  apex_score_max?: number
  momentum_score_min?: number
  momentum_score_max?: number
  insider_score_min?: number
  insider_score_max?: number
  sentiment_score_min?: number
  sentiment_score_max?: number
  // Boolean
  free_cash_flow_positive?: boolean
}

export interface PresetScreen {
  name: string
  description: string
  icon: string
  filters: ScreenFilters
}

export interface FilterField {
  name: string
  label: string
  type: 'number' | 'boolean' | 'select'
  group: string
  unit?: string
  min?: number
  max?: number
  options?: string[]
}

export interface ScreenResult {
  results: ScreenerStock[]
  count: number
  total: number
  page: number
  page_size: number
  total_pages: number
  filters_applied: ScreenFilters
  universe_size: number
}

export interface UniverseResult {
  stocks: ScreenerStock[]
  count: number
  total: number
  page: number
  page_size: number
  total_pages: number
  universe_tickers: number
}

export interface PresetsResult {
  presets: Record<string, PresetScreen>
  count: number
}

export interface FieldsResult {
  fields: FilterField[]
  grouped: Record<string, FilterField[]>
  total_fields: number
}

export interface SavedScreen {
  id: string
  name: string
  filters: ScreenFilters
  created_at: string
  updated_at: string
}

export interface SavedScreensResult {
  screens: SavedScreen[]
  count: number
}

// =============================================================================
// API Functions
// =============================================================================

export const screenerApi = {
  /**
   * Run custom stock screen with provided filters
   */
  async screenStocks(
    filters: ScreenFilters,
    sortBy: string = 'market_cap',
    sortOrder: 'asc' | 'desc' = 'desc',
    page: number = 1,
    pageSize: number = 50
  ): Promise<ScreenResult> {
    const params = new URLSearchParams({
      sort_by: sortBy,
      sort_order: sortOrder,
      page: String(page),
      page_size: String(pageSize),
    })
    const response = await apiClient.post(`/screener/screen?${params}`, filters)
    return response.data
  },

  /**
   * Get full stock universe with basic data
   */
  async getUniverse(
    sortBy: string = 'market_cap',
    sortOrder: 'asc' | 'desc' = 'desc',
    page: number = 1,
    pageSize: number = 50
  ): Promise<UniverseResult> {
    const response = await apiClient.get('/screener/universe', {
      params: { sort_by: sortBy, sort_order: sortOrder, page, page_size: pageSize }
    })
    return response.data
  },

  /**
   * Get all preset screen configurations
   */
  async getPresets(): Promise<PresetsResult> {
    const response = await apiClient.get('/screener/presets')
    return response.data
  },

  /**
   * Run a named preset screen
   */
  async runPreset(
    presetName: string,
    sortBy: string = 'market_cap',
    sortOrder: 'asc' | 'desc' = 'desc',
    page: number = 1,
    pageSize: number = 50
  ): Promise<ScreenResult & { preset: PresetScreen }> {
    const params = new URLSearchParams({
      sort_by: sortBy,
      sort_order: sortOrder,
      page: String(page),
      page_size: String(pageSize),
    })
    const response = await apiClient.post(`/screener/preset/${presetName}?${params}`)
    return response.data
  },

  /**
   * Get all supported filter fields with metadata
   */
  async getFilterFields(): Promise<FieldsResult> {
    const response = await apiClient.get('/screener/fields')
    return response.data
  },

  /**
   * Get available sectors
   */
  async getSectors(): Promise<{ sectors: string[] }> {
    const response = await apiClient.get('/screener/sectors')
    return response.data
  },

  /**
   * Get available industries
   */
  async getIndustries(sector?: string): Promise<{ industries: string[] }> {
    const response = await apiClient.get('/screener/industries', { params: { sector } })
    return response.data
  },

  /**
   * Get saved screens for current user
   */
  async getSavedScreens(): Promise<SavedScreensResult> {
    const response = await apiClient.get('/screener/saved')
    return response.data
  },

  /**
   * Save a new screen
   */
  async saveScreen(name: string, filters: ScreenFilters): Promise<{ screen: SavedScreen; message: string }> {
    const response = await apiClient.post('/screener/saved', { name, filters })
    return response.data
  },

  /**
   * Update an existing saved screen
   */
  async updateScreen(screenId: string, name: string, filters: ScreenFilters): Promise<{ screen: SavedScreen; message: string }> {
    const response = await apiClient.put(`/screener/saved/${screenId}`, { name, filters })
    return response.data
  },

  /**
   * Delete a saved screen
   */
  async deleteScreen(screenId: string): Promise<{ message: string }> {
    const response = await apiClient.delete(`/screener/saved/${screenId}`)
    return response.data
  },

  /**
   * Export results as CSV
   */
  async exportCSV(filters: ScreenFilters, sortBy: string = 'market_cap', sortOrder: string = 'desc'): Promise<Blob> {
    const params = new URLSearchParams({ sort_by: sortBy, sort_order: sortOrder })
    const response = await apiClient.post(`/screener/export/csv?${params}`, filters, {
      responseType: 'blob'
    })
    return response.data
  },

  /**
   * Run quick predefined screen
   */
  async quickScreen(
    screenType: string,
    sortBy: string = 'market_cap',
    sortOrder: 'asc' | 'desc' = 'desc',
    page: number = 1,
    pageSize: number = 50
  ): Promise<ScreenResult> {
    const response = await apiClient.get(`/screener/quick/${screenType}`, {
      params: { sort_by: sortBy, sort_order: sortOrder, page, page_size: pageSize }
    })
    return response.data
  }
}

// =============================================================================
// Natural Language Screener (Prompt 13)
// =============================================================================

export interface NLFilterLabel {
  field: string
  operator: string
  value: string
  display_label: string
}

export interface NLParseResult {
  query: string
  filters: ScreenFilters
  filter_labels: NLFilterLabel[]
  explanation: string
  confidence: number
  warnings: string[]
  generated_at: string
}

export interface NLScreenResult extends ScreenResult {
  nl_result: NLParseResult
}

export interface NLDetectResult {
  query: string
  is_screener_query: boolean
  confidence: number
}

export const nlScreenerApi = {
  /**
   * Parse a natural language query into structured filters
   */
  async parseQuery(query: string): Promise<NLParseResult> {
    const response = await apiClient.post('/screener/nl/parse', { query })
    return response.data
  },

  /**
   * Parse and run a natural language screen in one call
   */
  async nlScreen(
    query: string,
    sortBy: string = 'market_cap',
    sortOrder: 'asc' | 'desc' = 'desc',
    page: number = 1,
    pageSize: number = 50
  ): Promise<NLScreenResult> {
    const params = new URLSearchParams({
      sort_by: sortBy,
      sort_order: sortOrder,
      page: String(page),
      page_size: String(pageSize),
    })
    const response = await apiClient.post(`/screener/nl/screen?${params}`, { query })
    return response.data
  },

  /**
   * Get suggestion chips for the NL screener UI
   */
  async getSuggestions(): Promise<{ suggestions: string[] }> {
    const response = await apiClient.get('/screener/nl/suggestions')
    return response.data
  },

  /**
   * Detect if a query looks like a stock screener query
   */
  async detectScreenerQuery(query: string): Promise<NLDetectResult> {
    const response = await apiClient.post('/screener/nl/detect', { query })
    return response.data
  }
}

export default screenerApi
